import React, { useState, useEffect } from 'react';
import { getAllSystemConfig, updateSystemConfig } from './api';
import ContactosGuardia from './ContactosGuardia';
import './Config.css';

// Sección propia (no viene de system_configuration)
const GUARDIA_CONTACTS_SECTION = 'guardia-contacts';

function Config() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [activeSection, setActiveSection] = useState('redmine');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllSystemConfig();
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      setError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async (key) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const value = editedValues[key];
      if (value === undefined || value === '') {
        setError('El valor no puede estar vacío');
        return;
      }

      await updateSystemConfig(key, value);

      setSuccess(`Configuración "${key}" actualizada correctamente`);

      // Recargar configuración
      await loadConfig();

      // Limpiar valor editado
      setEditedValues(prev => {
        const newValues = { ...prev };
        delete newValues[key];
        return newValues;
      });

      // Auto-limpiar mensaje de éxito después de 3 segundos
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving config:', error);
      setError(error.response?.data?.error || 'Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const getCategoryTitle = (category) => {
    const titles = {
      'assistant': 'Asistente AI (Helpdesk)',
      'redmine': 'Integración con Redmine',
      'smtp': 'Configuración SMTP',
      'webhooks': 'Webhooks',
      'notifications': 'Notificaciones',
      [GUARDIA_CONTACTS_SECTION]: 'Contactos de guardia'
    };
    return titles[category] || category;
  };

  const getCategoryDescription = (category) => {
    const descriptions = {
      'assistant': 'Configuración del asistente AI Helpdesk powered by Claude API',
      'redmine': 'Configuración de la integración con Redmine para crear issues y obtener reportes',
      'smtp': 'Configuración del servidor SMTP para envío de correos electrónicos',
      'webhooks': 'Webhooks para integración con servicios externos como Google Chat',
      'notifications': 'Activar o desactivar diferentes tipos de notificaciones',
      [GUARDIA_CONTACTS_SECTION]: 'Teléfonos y extensiones de Zadarma del equipo de guardia y contactos especiales'
    };
    return descriptions[category] || '';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'assistant': '🤖',
      'redmine': '🔗',
      'smtp': '✉️',
      'webhooks': '🔔',
      'notifications': '🔊',
      [GUARDIA_CONTACTS_SECTION]: '📞'
    };
    return icons[category] || '⚙️';
  };

  const renderConfigInput = (configItem) => {
    const currentValue = editedValues[configItem.config_key] !== undefined
      ? editedValues[configItem.config_key]
      : configItem.config_value;

    const hasChanges = editedValues[configItem.config_key] !== undefined;

    if (configItem.config_type === 'boolean') {
      return (
        <div className="config-item" key={configItem.config_key}>
          <div className="config-item-header">
            <label className="config-label">
              <input
                type="checkbox"
                checked={currentValue === 'true' || currentValue === true}
                onChange={(e) => handleInputChange(configItem.config_key, e.target.checked.toString())}
                className="config-checkbox"
              />
              <span className="config-name">{configItem.description}</span>
            </label>
          </div>
          <small className="config-hint">Key: {configItem.config_key}</small>
          {hasChanges && (
            <button
              onClick={() => handleSave(configItem.config_key)}
              disabled={saving}
              className="btn-save-small"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      );
    }

    if (configItem.config_type === 'number') {
      return (
        <div className="config-item" key={configItem.config_key}>
          <label className="config-label">{configItem.description}</label>
          <small className="config-hint">Key: {configItem.config_key}</small>
          <div className="config-input-row">
            <input
              type="number"
              value={currentValue}
              onChange={(e) => handleInputChange(configItem.config_key, e.target.value)}
              className="config-input"
              placeholder={configItem.description}
            />
            {hasChanges && (
              <button
                onClick={() => handleSave(configItem.config_key)}
                disabled={saving}
                className="btn-save"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      );
    }

    // String (default)
    if (configItem.is_sensitive) {
      return (
        <div className="config-item" key={configItem.config_key}>
          <label className="config-label">{configItem.description}</label>
          <small className="config-hint">Key: {configItem.config_key}</small>
          {configItem.has_value && !hasChanges && (
            <small className="config-hint-value">Valor configurado (oculto por seguridad)</small>
          )}
          <div className="config-input-row">
            <input
              type={showPasswords[configItem.config_key] ? 'text' : 'password'}
              value={currentValue}
              onChange={(e) => handleInputChange(configItem.config_key, e.target.value)}
              className="config-input"
              placeholder={configItem.has_value ? '********' : 'Introducir valor'}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility(configItem.config_key)}
              className="btn-toggle-password"
              title={showPasswords[configItem.config_key] ? 'Ocultar' : 'Mostrar'}
            >
              {showPasswords[configItem.config_key] ? '🙈' : '👁️'}
            </button>
            {hasChanges && (
              <button
                onClick={() => handleSave(configItem.config_key)}
                disabled={saving}
                className="btn-save"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="config-item" key={configItem.config_key}>
        <label className="config-label">{configItem.description}</label>
        <small className="config-hint">Key: {configItem.config_key}</small>
        <div className="config-input-row">
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleInputChange(configItem.config_key, e.target.value)}
            className="config-input"
            placeholder={configItem.description}
          />
          {hasChanges && (
            <button
              onClick={() => handleSave(configItem.config_key)}
              disabled={saving}
              className="btn-save"
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="config-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="config-container">
      <div className="config-layout">
        {/* Sidebar Navigation */}
        <div className="config-sidebar">
          <div className="config-sidebar-header">
            <h2>⚙️ Configuración</h2>
            <p>Gestión del sistema</p>
          </div>
          <nav className="config-nav">
            {[...Object.keys(config), GUARDIA_CONTACTS_SECTION].map(category => (
              <button
                key={category}
                className={`config-nav-item ${activeSection === category ? 'active' : ''}`}
                onClick={() => setActiveSection(category)}
              >
                <span className="config-nav-icon">{getCategoryIcon(category)}</span>
                <span className="config-nav-text">{getCategoryTitle(category)}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        <div className="config-main">
          <div className="config-header">
            <h1>{getCategoryIcon(activeSection)} {getCategoryTitle(activeSection)}</h1>
            <p className="subtitle">{getCategoryDescription(activeSection)}</p>
          </div>

          {activeSection === GUARDIA_CONTACTS_SECTION ? (
            <ContactosGuardia />
          ) : (
            <>
              {error && (
                <div className="alert alert-error">
                  <span className="alert-icon">⚠️</span>
                  {error}
                  <button className="alert-close" onClick={() => setError(null)}>×</button>
                </div>
              )}

              {success && (
                <div className="alert alert-success">
                  <span className="alert-icon">✅</span>
                  {success}
                  <button className="alert-close" onClick={() => setSuccess(null)}>×</button>
                </div>
              )}

              <div className="config-section-content">
                {config[activeSection] && config[activeSection].map(configItem => renderConfigInput(configItem))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Config;
